/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, MonthRange } from '../../types/schedule.types';
import {
	getMonthWeeks,
	getWeekDates,
	getWeekStart,
	isSameDay,
	toDateKey,
} from '../../utils/dateUtils';
import icon from '../../assets/Schedule/Vector.png';
import MonthDayCell from './MonthDayCell';
import DayActivitiesModal from './DayActivitiesModal';
import styles from '../../styles/Schedule/MonthlySchedule.module.css';

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thr', 'Fri', 'Sat', 'Sun'];

interface MonthlyScheduleProps {
	selectedMonth: MonthRange;
	activitiesByDate: Map<string, Activity[]>;
	loading?: boolean;
	onActivityClick?: (activity: Activity) => void;
}

const MonthlySchedule: React.FC<MonthlyScheduleProps> = ({
	selectedMonth,
	activitiesByDate,
	loading = false,
	onActivityClick,
}) => {
	const [expandedDate, setExpandedDate] = useState<Date | null>(null);
	const bodyRef = useRef<HTMLDivElement | null>(null);
	const currentWeekRef = useRef<HTMLDivElement | null>(null);

	// Natural order — no rotation/shuffle.
	const weeks = useMemo(() => getMonthWeeks(selectedMonth), [selectedMonth]);

	// Index of the row containing today (or -1 if today isn't in this month).
	const currentWeekIndex = useMemo(() => {
		const today = new Date();
		const containsToday =
			today.getMonth() === selectedMonth.month &&
			today.getFullYear() === selectedMonth.year;
		if (!containsToday) return -1;
		const currentStart = getWeekStart(today);
		return weeks.findIndex(w => isSameDay(w.start, currentStart));
	}, [weeks, selectedMonth]);

	const activitiesFor = (date: Date): Activity[] =>
		activitiesByDate.get(toDateKey(date)) ?? [];

	const isMonthEmpty = weeks.every(week =>
		getWeekDates(week).every(date => activitiesFor(date).length === 0)
	);

	useEffect(() => {
		if (loading) return;
		const body = bodyRef.current;
		const row = currentWeekRef.current;
		if (!body || !row || currentWeekIndex <= 0) {
			if (body) body.scrollTop = 0;
			return;
		}
		body.scrollTop = row.offsetTop - body.offsetTop;
	}, [currentWeekIndex, selectedMonth, loading]);

	if (loading) {
		return (
			<div className={styles.loading}>
				<div className={styles.spinner} />
				<span>Loading schedule…</span>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.headerRow}>
				{DAY_HEADERS.map(d => (
					<div key={d} className={styles.headerCell}>
						{d}
					</div>
				))}
			</div>

			<div className={styles.body} ref={bodyRef}>
				{weeks.map((week, wi) => {
					const isCurrentWeek = wi === currentWeekIndex;
					return (
						<div
							key={wi}
							ref={isCurrentWeek ? currentWeekRef : undefined}
							className={`${styles.weekRow} ${isCurrentWeek ? styles.weekRowCurrent : ''}`}
						>
							{getWeekDates(week).map(date => (
								<MonthDayCell
									key={toDateKey(date)}
									date={date}
									isCurrentMonth={
										date.getMonth() ===
										selectedMonth.month &&
										date.getFullYear() ===
										selectedMonth.year
									}
									activities={activitiesFor(date)}
									onShowMore={setExpandedDate}
									onActivityClick={onActivityClick}
								/>
							))}
						</div>
					);
				})}

				{isMonthEmpty && (
					<div className={styles.emptyOverlay}>
						<div className={styles.emptyState}>
							<div className={styles.emptyIcon}>
								<img
									className={styles.icon}
									src={icon}
									alt=""
								/>
							</div>
							<p className={styles.emptyText}>
								No activities for this month
							</p>
						</div>
					</div>
				)}
			</div>

			<DayActivitiesModal
				date={expandedDate}
				activities={expandedDate ? activitiesFor(expandedDate) : []}
				onClose={() => setExpandedDate(null)}
				onActivityClick={onActivityClick}
			/>
		</div>
	);
};

export default MonthlySchedule;
